import React, { useContext, useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import CartContext from 'core/providers/CartContext';
import MpOrderContext from 'core/providers/MpOrderContext';
import ProcessorContext from 'core/providers/ProcessorContext';
import { getCartCount } from 'cart/utils';
import EmptyCart from 'cart/components/EmptyCart';
import CartPlaceholders from 'cart/components/CartPlaceholders';
import loadable from '@loadable/component';
const CartCheckoutPage = loadable(() => import('./CartCheckoutPage'), {
	fallback: <CartPlaceholders />
});

/**
 * Render the `CartCheckoutPageContainer`
 * In the old days, we'd `checkout_page.js` to fetch data & start `checkout_router.js`
 *
 * SSR will only render Placeholders
 * Data is fetched on front-end via ContextProviders
 * Then `CartCheckoutPage` renders the checkout steps
 *
 * @context {CartContext} load cart to build order with
 * @context {MpOrderContext} used to build order request or display confirmation details
 */
const CartCheckoutPageContainer = () => {
	const [ stripePromise, setStripePromise ] = useState(null);
	const { loading: cartLoading, getActiveCarts } = useContext(CartContext);
	const { mpOrderId, loading } = useContext(MpOrderContext);
	const { clientSecret, stripePublicKey } = useContext(ProcessorContext);

	const hasStripe = stripePromise !== null;
	const hasMpOrder = !!mpOrderId;
	useEffect(() => {
		if (hasMpOrder || hasStripe || !stripePublicKey) return;

		// Make sure to call `loadStripe` inside effect to avoid
		// recreating the `Stripe` object on every render.
		const prom = loadStripe(stripePublicKey);
		setStripePromise(prom);

	}, [hasMpOrder, hasStripe, stripePublicKey, mpOrderId]);

	// With mpOrderId, wait for (completed) order to load
	// Otherwise, wait for (incomplete) cart(s) to load
	const isLoading = mpOrderId ? loading : cartLoading;
	if (isLoading) return <CartPlaceholders />;

	if (!mpOrderId) {
		// Before order is placed... Check active carts
		const carts = getActiveCarts();
		if (getCartCount(carts) === 0) {
			return <EmptyCart />;
		}
	}

	return (
		// Wrap with Stripe Elements provider
		// ref: https://stripe.com/docs/stripe-js/react#elements-provider
		// NOTE: `key` prop ensures the `Elements` provider is re-rendered when intent is "refreshed"
		<Elements key={clientSecret} stripe={stripePromise}>
			<CartCheckoutPage />
		</Elements>
	);
};

export default CartCheckoutPageContainer;
